import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { SubjectType } from '../lib/api';

/**
 * What record the page currently on screen is showing.
 *
 * ⭐⭐ PUBLISHED BY THE PAGE, NEVER DERIVED FROM THE ROUTE. The facts below are the data the
 * page has ALREADY LOADED and is ALREADY DISPLAYING, so the block the model receives cannot
 * disagree with what the operator is looking at. A rail that re-fetched by id could hand the
 * model a different deal value than the one on screen, which is the defect class
 * docs/CURRENT-STATE.md names as dominating this build - one part of the app modelling a deal
 * differently from another, eight occurrences, none caught by tsc or a green suite.
 *
 * Route-derivation was also rejected because it needs a second copy of the route table, and
 * subjectResolver.ts already warns that a route/href disagreement renders a BLANK PAGE with no
 * console error.
 */
export interface PageSubject {
  type: SubjectType;
  id: string;
  /** What a human calls it. Also stored as the chat's page_context, so /ai history is readable. */
  label: string;
  /** Reuses the { label, value } pair shape the brochure prefill uses for suggestedFacts. */
  facts: { label: string; value: string }[];
  /**
   * ⛔⛔ WHETHER THE RECORD OWNS A BUILDING (the 113th). The rail drops the model's building facts on a record page because
   *    the page owns which building it is - but a DEAL with no property attached owns none, and the drop threw away the
   *    address he stated (live, his Test deal: the letter opened empty while the chat said it was filled in). `false`
   *    ONLY there; omitted means the record owns its building, so a page that forgets to say keeps the strip.
   */
  ownsBuilding?: boolean;
}

interface Ctx {
  subject: PageSubject | null;
  publish: (s: PageSubject | null) => void;
}

/**
 * ⚠️ Default is a working no-op context rather than `undefined`. `usePageSubject` is called by
 * the rail, which Layout mounts on every page; making the hook throw outside a provider would
 * turn a missing optional feature into a blank app.
 */
const PageSubjectCtx = createContext<Ctx>({ subject: null, publish: () => {} });

export function PageSubjectProvider({ children }: { children: React.ReactNode }) {
  const [subject, setSubject] = useState<PageSubject | null>(null);
  const publish = useCallback((s: PageSubject | null) => setSubject(s), []);
  return (
    <PageSubjectCtx.Provider value={{ subject, publish }}>{children}</PageSubjectCtx.Provider>
  );
}

/**
 * Called by a record page to say what it is showing.
 *
 * 🚨 THE CLEANUP IS LOAD-BEARING. Without it, navigating from a deal to the map leaves the rail
 * claiming the deal is still on screen, and the assistant answers about a record the operator
 * is no longer looking at.
 *
 * ⚠️ Keyed on the identity fields rather than the object, because a page that builds the
 * subject inline creates a new object every render and would otherwise loop.
 */
export function useSetPageSubject(subject: PageSubject | null): void {
  const { publish } = useContext(PageSubjectCtx);
  const key = subject
    ? `${subject.type}:${subject.id}:${subject.label}:${JSON.stringify(subject.facts)}:${String(subject.ownsBuilding)}`
    : null;
  useEffect(() => {
    publish(subject);
    return () => publish(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, publish]);
}

/** Read by the rail. Null means this page is not showing a record. */
export function usePageSubject(): PageSubject | null {
  return useContext(PageSubjectCtx).subject;
}
